import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { LoadingOutlined } from "@ant-design/icons";
import { Button } from "@/shared/components/button";
import { Textarea } from "@/shared/components/dialog";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PageSpinner } from "@/shared/components/spinner";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import {
  COMMENT_MAX_LENGTH,
  getReviewItemApi,
  submitReviewResponseApi,
  type ReviewAnswer,
  type ReviewItem,
} from "@/portals/patient/api/reviewItemsApi";
import { ISSUE_LABELS, ClinicianQuestionBadge, UnderReviewBadge } from "./medicationReviewBadges";
import { ReviewRecords, isPairItem } from "./ReviewConflictCard";
import { EFFECT_LABELS, outcomeActionLabel } from "./reviewOutcomeLabels";
import { PatientConversation, buildConversation } from "./PatientConversation";

export const ANSWER_LABELS: Record<ReviewAnswer, string> = {
  CURRENT_MEDICINE: "This is my current medicine",
  NO_LONGER_TAKE: "This is an old medicine I no longer take",
  DOSE_CHANGED: "My dose changed",
  TAKE_BOTH: "I take both of these",
  NOT_RECOGNIZED: "I don't recognize this medicine",
  NOT_SURE: "I'm not sure",
  SKIPPED: "Skipped, let a clinician decide",
  REPLY: "Replied to your clinician",
};

const OPTIONS: Exclude<ReviewAnswer, "SKIPPED" | "REPLY">[] = [
  "CURRENT_MEDICINE", "NO_LONGER_TAKE", "DOSE_CHANGED", "TAKE_BOTH", "NOT_RECOGNIZED", "NOT_SURE",
];

export const MESSAGES = {
  success: "Thank you. A clinician will review this.",
  replied: "Your reply has been sent to your clinician.",
  replyRequired: "Please type your reply to your clinician.",
  resolvedNoLongerTake: "Done — we've noted that you no longer take this medicine. Nothing else to do.",
  skipped: "Sent to your clinician without an answer. They'll decide.",
  alreadyAnswered: "This item has already been answered.",
  notFound: "This review item isn't available.",
  loadFailed: "Couldn't load this review item. Please try again.",
};

const BRAND_BUTTON =
  "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** A review item's records as Medications carrying their role, so ReviewRecords lays them out like the card. */
/** A review item's records as Medication values (shared with the Human Review Queue). */
export function toMedications(item: ReviewItem) {
  return item.records.map((r) =>
    adaptMedication({
      id: r.id,
      resourceType: "MedicationRequest",
      sourceVendor: r.sourceVendor,
      uploadId: null,
      confidenceScore: 1,
      needsReview: false,
      reviewReason: null,
      createdAt: r.createdAt,
      updatedAt: r.createdAt,
      fields: r.fields,
      rxcui: r.rxcui ?? null,
      review: { status: "UNDER_REVIEW", reviewItemId: item.id, recordRole: r.recordRole, triggerType: item.triggerType },
      origin: null,
    }),
  );
}

/** The item's Patient Conversation: the clinician's questions, the patient's answers, the decision. */
export function itemConversation(item: ReviewItem, isPair: boolean) {
  return buildConversation({
    questions: item.clinicianQuestions ?? (item.clinicianQuestion ? [item.clinicianQuestion] : []),
    responses: item.patientResponses,
    decisions: item.outcome
      ? [{ at: item.outcome.decidedAt, label: outcomeActionLabel(item.outcome.action, isPair), reason: item.outcome.reason }]
      : [],
  });
}

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; item: ReviewItem }
  | { kind: "outcome"; item: ReviewItem }
  | { kind: "success" }
  | { kind: "already-answered" }
  | { kind: "not-found" }
  | { kind: "load-failed" };

interface ReviewItemViewProps {
  itemId: string;
  /** Own dependent's id when acting as them; undefined for the patient's own items. */
  actingAsProfileId?: string;
  onBack: () => void;
  /** After the item changed server-side (sent, or found already answered): refetch records + count. */
  onItemChanged: () => void;
  /** "modal": shown inside the Medications tab's review dialog — no page
   *  chrome, and "Close" instead of "Back to medications". */
  variant?: "page" | "modal";
}

function StateCard({ icon, tone, message, onBack, backLabel = "Back to medications" }: {
  icon: string; tone: "success" | "neutral"; message: string; onBack: () => void; backLabel?: string;
}) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-[#E5E7EB] bg-white px-6 py-12 text-center shadow-sm" role="status">
      <div className={cn("flex h-14 w-14 items-center justify-center rounded-full", tone === "success" ? "bg-[#ECFDF3]" : "bg-[#F3F4F6]")}>
        <Icon icon={icon} className={cn("h-7 w-7", tone === "success" ? "text-[#027A48]" : "text-[#9CA3AF]")} />
      </div>
      <p className="mt-4 text-base font-extrabold text-[#0A2947]">{message}</p>
      <Button onClick={onBack} className={cn(BRAND_BUTTON, "!mt-6")}>{backLabel}</Button>
    </div>
  );
}

const CORRECTED_FIELD_LABELS: Record<string, string> = {
  medication_name: "name", dose_quantity: "dose", dose_unit: "dose unit", frequency: "frequency",
  route: "route", dosage_text: "instructions", status: "status",
};

const EFFECT_PILL: Record<string, string> = {
  KEPT: "bg-[#ECFDF3] text-[#027A48]",
  CORRECTED: "bg-[#ECFDF3] text-[#027A48]",
  DISCONTINUED: "bg-[#F3F4F6] text-[#4B5563]",
  DISMISSED: "bg-[#FDECEF] text-[#E4002B]",
};

/** A resolved item (Module 5): the clinician's decision and what it did to each record. No answer form. */
function OutcomeView({ item, onBack }: { item: ReviewItem; onBack: () => void }) {
  const meds = toMedications(item);
  const isPair = isPairItem(meds);
  const effectById = new Map(item.outcome?.effects.map((e) => [e.recordId, e.effect]) ?? []);
  // Corrected values are already in each record's fields; correctedFields says which ones.
  const correctedById = new Map(item.records.map((r) => [r.id, r.correctedFields ?? []]));
  return (
    <div className="mx-auto grid max-w-4xl gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
          <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
        </div>
        <h2 className="text-lg font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</h2>
      </div>

      <section aria-label="Clinician's decision" className="rounded-2xl border border-[#027A48]/25 bg-[#ECFDF3] p-4">
        <div className="flex items-start gap-3">
          <Icon icon="lucide:badge-check" className="mt-0.5 h-5 w-5 shrink-0 text-[#027A48]" />
          <div>
            <p className="text-sm font-extrabold text-[#0A2947]">Clinician's decision</p>
            {item.outcome ? (
              <>
                <p className="mt-1 text-sm font-semibold text-[#0A2947]">{outcomeActionLabel(item.outcome.action, isPair)}</p>
                <p className="mt-1 text-sm text-[#374151]">"{item.outcome.reason}"</p>
                <p className="mt-1.5 text-xs text-[#6B7280]">{formatDate(item.outcome.decidedAt)}</p>
              </>
            ) : (
              <p className="mt-1 text-sm text-[#374151]">A clinician has reviewed this item.</p>
            )}
          </div>
        </div>
      </section>

      <ReviewRecords
        records={meds}
        renderExtra={(med) => {
          const effect = effectById.get(med.id);
          const corrected = effect === "CORRECTED" ? correctedById.get(med.id) ?? [] : [];
          return effect ? (
            <>
              <p className="!mb-0 mt-3" data-testid="record-effect">
                <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold", EFFECT_PILL[effect])}>
                  {EFFECT_LABELS[effect]}
                </span>
              </p>
              {corrected.length > 0 && (
                <p className="!mb-0 mt-1 text-xs text-[#027A48]" data-testid="record-corrected-fields">
                  Corrected: {corrected.map((f) => CORRECTED_FIELD_LABELS[f] ?? f.replace(/_/g, " ")).join(", ")}
                </p>
              )}
            </>
          ) : null;
        }}
      />

      {(item.patientResponses.length > 0 || (item.clinicianQuestions ?? []).length > 0) && (
        <PatientConversation messages={itemConversation(item, isPair)} viewer="patient" />
      )}

      <div className="flex justify-end">
        <Button onClick={onBack} className={BRAND_BUTTON}>Back to medications</Button>
      </div>
    </div>
  );
}

export function ReviewItemView({ itemId, actingAsProfileId, onBack, onItemChanged, variant = "page" }: ReviewItemViewProps) {
  const isModal = variant === "modal";
  const backLabel = isModal ? "Close" : "Back to medications";
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [answer, setAnswer] = useState<ReviewAnswer | null>(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [skipping, setSkipping] = useState(false); // which button gets the spinner
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getReviewItemApi(itemId, actingAsProfileId).then(({ data, error }) => {
      if (cancelled) return;
      if (data) {
        setState(
          data.itemStatus === "RESOLVED_CLINICIAN_VERIFIED"
            ? { kind: "outcome", item: data }
            : (data.canRespond ?? data.awaitingInput) ? { kind: "ready", item: data } : { kind: "already-answered" },
        );
      }
      else if (error?.code === "REVIEW_ITEM_NOT_FOUND") setState({ kind: "not-found" });
      else setState({ kind: "load-failed" });
    });
    return () => {
      cancelled = true;
    };
  }, [itemId, actingAsProfileId]);

  useEffect(() => load(), [load]);

  const item = state.kind === "ready" ? state.item : null;
  const meds = useMemo(() => (item ? toMedications(item) : []), [item]);
  const isPair = isPairItem(meds);
  const options = OPTIONS.filter((o) => o !== "TAKE_BOTH" || isPair);
  // A clinician's question is waiting: the reply text is required and the
  // answer choices become optional (no choice → sent as a plain REPLY).
  const replying = Boolean(item?.clinicianQuestion);
  const canSend = replying ? comment.trim().length > 0 : Boolean(answer);

  // Sent straight away — no second "are you sure?" step. A skip goes on to the
  // clinician WITHOUT any comment, even if one was typed.
  const send = async (chosen: ReviewAnswer) => {
    if (!item) return;
    if (replying && chosen !== "SKIPPED" && !comment.trim()) {
      setFormError(MESSAGES.replyRequired);
      return;
    }
    setSending(true);
    setSkipping(chosen === "SKIPPED");
    const trimmed = chosen === "SKIPPED" ? "" : comment.trim();
    const { data: submitted, error } = await submitReviewResponseApi(
      item.id,
      { answer: chosen, ...(trimmed ? { comment: trimmed } : {}) },
      actingAsProfileId,
    );
    setSending(false);
    if (!error) {
      // Confirmed with a toast (no medicine names), then the review closes.
      toastSuccess(
        submitted?.itemStatus === "RESOLVED_PATIENT_REPORTED" ? MESSAGES.resolvedNoLongerTake
          : chosen === "SKIPPED" ? MESSAGES.skipped
          : replying ? MESSAGES.replied
          : MESSAGES.success,
      );
      onItemChanged();
      onBack();
      return;
    }
    if (error.code === "ITEM_NOT_AWAITING_INPUT") {
      setState({ kind: "already-answered" });
      onItemChanged();
    } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
      setState({ kind: "not-found" });
    } else if (error.code === "VALIDATION_ERROR") {
      setFormError(error.message);
    } else {
      toastError(error.message);
    }
  };

  const body = (() => {
    switch (state.kind) {
      case "loading":
        return <PageSpinner className="bg-transparent" />;
      case "already-answered":
        return <StateCard icon="lucide:clipboard-check" tone="neutral" message={MESSAGES.alreadyAnswered} onBack={onBack} backLabel={backLabel} />;
      case "not-found":
        return <StateCard icon="lucide:file-x" tone="neutral" message={MESSAGES.notFound} onBack={onBack} backLabel={backLabel} />;
      case "load-failed":
        return <StateCard icon="lucide:wifi-off" tone="neutral" message={MESSAGES.loadFailed} onBack={onBack} backLabel={backLabel} />;
      case "outcome":
        return <OutcomeView item={state.item} onBack={onBack} />;
      case "ready":
        return null;
    }
  })();

  return (
    <div className={isModal ? "" : "min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5"}>
      {!isModal && <PageBackButton onClick={onBack} ariaLabel="Back to medications" className="!mb-5" />}
      {body ?? (item && (
        <div className={isModal ? "grid gap-6" : "mx-auto grid max-w-4xl gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm"}>
          {/* b) Issue label */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
                <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
              </div>
              <h2 className="text-lg font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</h2>
            </div>
            {item.itemStatus === "NEEDS_MORE_INFO" ? <ClinicianQuestionBadge /> : <UnderReviewBadge />}
          </div>

          {/* c) Records */}
          <ReviewRecords records={meds} />

          {/* Patient Conversation: the clinician's questions and your earlier answers */}
          {(item.patientResponses.length > 0 || (item.clinicianQuestions ?? []).length > 0 || item.clinicianQuestion) && (
            <PatientConversation
              messages={itemConversation(item, isPair)}
              viewer="patient"
              waitingFor={item.itemStatus === "NEEDS_MORE_INFO" ? "patient" : null}
            />
          )}

          {/* Your reply to the clinician's question — required */}
          {replying && (
            <section>
              <label htmlFor="review-reply" className="mb-2 block text-sm font-extrabold text-[#0A2947]">
                Your reply to your clinician
              </label>
              <Textarea
                id="review-reply"
                value={comment}
                onChange={(e) => { setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH)); setFormError(null); }}
                maxLength={COMMENT_MAX_LENGTH}
                showCount
                rows={4}
                placeholder="Type your answer to the question above"
                className="!rounded-lg"
              />
            </section>
          )}

          {/* d) Question */}
          <section>
            <p id="review-question" className="mb-3 text-sm font-extrabold text-[#0A2947]">
              Which of these best describes this medicine?
              {replying && <span className="ml-1 text-xs font-semibold text-[#9CA3AF]">(optional)</span>}
            </p>
            <div role="radiogroup" aria-labelledby="review-question" className="grid gap-3 sm:grid-cols-2">
              {options.map((option) => {
                const selected = answer === option;
                return (
                  <Button
                    key={option}
                    variant="outline"
                    role="radio"
                    aria-checked={selected}
                    // Picking the selected choice again clears it (it's optional when replying).
                    onClick={() => { setAnswer(replying && selected ? null : option); setFormError(null); }}
                    className={cn(
                      "!flex !h-auto !w-full !items-center !justify-start !gap-3 !whitespace-normal !rounded-2xl !px-4 !py-3.5 !text-left !text-sm !font-semibold !text-[#0A2947]",
                      selected
                        ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4] hover:!text-[#0A2947]"
                        : "!border-[#DDE1E8] !bg-white hover:!border-[#F17893]/50 hover:!bg-white hover:!text-[#0A2947]",
                    )}
                  >
                    <Icon
                      icon={selected ? "lucide:circle-dot" : "lucide:circle"}
                      className={cn("h-4 w-4 shrink-0", selected ? "text-[#E4002B]" : "text-[#9CA3AF]")}
                    />
                    <span>
                      {ANSWER_LABELS[option]}
                      {option === "NO_LONGER_TAKE" && (
                        <span className="block text-xs font-normal text-[#6B7280]">Closes this review right away</span>
                      )}
                    </span>
                  </Button>
                );
              })}
            </div>
          </section>

          {/* e) Comment (the reply box above takes its place while replying) */}
          {!replying && <section>
            <label htmlFor="review-comment" className="mb-2 block text-sm font-semibold text-[#0A2947]">
              Anything else your clinician should know?
            </label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
              maxLength={COMMENT_MAX_LENGTH}
              showCount
              rows={4}
              placeholder="Optional"
              className="!rounded-lg"
            />
          </section>}

          {formError && <p role="alert" className="-mt-3 text-xs text-[#E4002B]">{formError}</p>}

          {/* f) Actions */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* Skip: on to the clinician as it is — no answer, no comment. */}
            <Button
              variant="ghost"
              disabled={sending}
              aria-busy={sending && skipping}
              onClick={() => void send("SKIPPED")}
              className="!relative !h-10 !rounded-lg !px-4 !text-sm !font-semibold !text-[#4B5563] hover:!bg-[#F3F4F6] hover:!text-[#E4002B]"
            >
              <span className={cn(sending && skipping && "invisible")}>Skip, let a clinician decide</span>
              {sending && skipping && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <LoadingOutlined aria-hidden />
                </span>
              )}
            </Button>
            {/* While sending: unclickable, spinner centred, same size (label kept but hidden). */}
            <Button
              disabled={!canSend || sending}
              aria-busy={sending && !skipping}
              onClick={() => void send(answer ?? "REPLY")}
              className={cn(BRAND_BUTTON, "!relative")}
            >
              <span className={cn(sending && !skipping && "invisible")}>{replying ? "Send reply" : "Send"}</span>
              {sending && !skipping && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <LoadingOutlined aria-hidden />
                </span>
              )}
            </Button>
          </div>
        </div>
      ))}

    </div>
  );
}
